import { Menu } from '@base-ui/react/menu';
import { Check, ChevronsUpDown, Landmark, Star } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { isApiError } from '@core/api/errors';
import { useCurrentUser, useSetDefaultOrganization } from '@core/auth';
import { useTranslation } from '@core/i18n';
import { useSwitchOrganization } from '@core/org';
import { cn } from '@core/utils/cn';

interface OrganizationSwitcherProps {
  collapsed?: boolean;
  /** Called after a choice is made, so the mobile navigation sheet can close itself. */
  onNavigate?: () => void;
}

const ITEM = cn(
  'flex min-w-0 cursor-default items-center gap-2 rounded-md px-2.5 py-2 text-[14px] outline-none select-none',
  'data-highlighted:bg-fill-subtle data-highlighted:text-fg',
  'data-disabled:pointer-events-none data-disabled:opacity-40',
);

/**
 * Which organization this tab is acting in, and the way to another one.
 *
 * <p>A menu in the rail's footer rather than in a top bar: on desktop there is no top bar,
 * and the footer is already where "who am I, in what" lives. It survives collapse as an
 * icon and rides along into the mobile navigation sheet unchanged.
 *
 * <p>The star beside each organization is its own menu item, not a button nested inside
 * one: a control inside a `menuitem` is unreachable by keyboard and announced as nothing.
 * Two items per row keeps both actions in the menu's roving focus.
 *
 * <p>Hidden entirely for the common case -- one organization and no platform role --
 * because a switcher with nothing to switch to is a control that only ever says no.
 */
export function OrganizationSwitcher({ collapsed = false, onNavigate }: OrganizationSwitcherProps) {
  const me = useCurrentUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const switchOrganization = useSwitchOrganization();
  const setDefault = useSetDefaultOrganization();
  const [defaultError, setDefaultError] = useState<string | null>(null);

  if (me.memberships.length <= 1 && !me.platformAdmin) return null;

  const current = me.organization ?? null;
  const currentName = current?.name ?? t.nav.platform;
  // A platform admin inside a customer's organization is there on platform access, not
  // as a member, so it is missing from their memberships and is listed on its own.
  const visiting =
    current && !me.memberships.some((m) => m.organizationId === current.id) ? current : null;

  const choose = (organizationId: string) => {
    onNavigate?.();
    if (organizationId === current?.id) return;
    void switchOrganization(organizationId);
  };

  const makeDefault = (organizationId: string) => {
    setDefaultError(null);
    setDefault.mutate(organizationId, {
      onError: (error) =>
        setDefaultError(
          isApiError(error) && error.message ? error.message : t.switcher.setDefaultFailed,
        ),
    });
  };

  return (
    <Menu.Root onOpenChange={(open) => open && setDefaultError(null)}>
      <Menu.Trigger
        aria-label={current ? t.switcher.current(current.name) : t.switcher.label}
        className={cn(
          'text-fg flex h-10 w-full min-w-0 items-center gap-2 rounded-md px-2.5 text-[13px]',
          'hover:bg-fill-subtle transition-colors duration-150',
          'focus-visible:outline-accent outline-none focus-visible:outline-2 focus-visible:outline-offset-2',
          collapsed && 'mx-auto w-10 justify-center px-0',
        )}
      >
        <Landmark className="text-fg-muted size-4 shrink-0" aria-hidden />
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1 truncate text-left font-medium">{currentName}</span>
            <ChevronsUpDown className="text-fg-subtle size-4 shrink-0" aria-hidden />
          </>
        )}
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner
          side={collapsed ? 'right' : 'top'}
          align="start"
          sideOffset={8}
          className="z-50"
        >
          <Menu.Popup
            className={cn(
              'rounded-card bg-overlay shadow-e2 w-72 max-w-[calc(100vw-2rem)] origin-(--transform-origin) p-1.5',
              'max-h-(--available-height) overflow-y-auto',
              'ring-separator ring-1 ring-inset',
              'ease-spring transition-[transform,opacity] duration-200',
              'data-[starting-style]:scale-95 data-[starting-style]:opacity-0',
              'data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
              'motion-reduce:transition-none',
            )}
          >
            <Menu.Group>
              <Menu.GroupLabel className="text-fg-subtle px-2.5 pt-1.5 pb-1 text-[11px] font-semibold tracking-[0.06em] uppercase">
                {t.switcher.heading}
              </Menu.GroupLabel>

              {visiting && (
                <Menu.Item className={ITEM} onClick={() => choose(visiting.id)}>
                  <Check className="text-accent size-4 shrink-0" aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{visiting.name}</span>
                    <span className="text-fg-subtle block text-[12px]">
                      {t.switcher.platformAccess}
                    </span>
                  </span>
                </Menu.Item>
              )}

              {me.memberships.map((membership) => {
                const isCurrent = membership.organizationId === current?.id;
                return (
                  <div key={membership.organizationId} className="flex items-center gap-1">
                    <Menu.Item
                      className={cn(ITEM, 'flex-1')}
                      onClick={() => choose(membership.organizationId)}
                    >
                      {isCurrent ? (
                        <Check className="text-accent size-4 shrink-0" aria-hidden />
                      ) : (
                        <span className="size-4 shrink-0" />
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{membership.name}</span>
                        <span className="text-fg-subtle block text-[12px]">
                          {t.role[membership.role]}
                          {membership.isDefault && ` · ${t.switcher.defaultBadge}`}
                        </span>
                      </span>
                    </Menu.Item>
                    <Menu.Item
                      closeOnClick={false}
                      disabled={membership.isDefault || setDefault.isPending}
                      aria-label={
                        membership.isDefault
                          ? t.switcher.isDefault(membership.name)
                          : t.switcher.setDefault(membership.name)
                      }
                      className={cn(ITEM, 'size-9 shrink-0 justify-center p-0')}
                      onClick={() => makeDefault(membership.organizationId)}
                    >
                      <Star
                        aria-hidden
                        className={cn(
                          'size-4',
                          membership.isDefault ? 'fill-caution text-caution' : 'text-fg-subtle',
                        )}
                      />
                    </Menu.Item>
                  </div>
                );
              })}
            </Menu.Group>

            {me.platformAdmin && (
              <>
                <div aria-hidden className="border-separator my-1 border-t" />
                <Menu.Item
                  className={ITEM}
                  onClick={() => {
                    onNavigate?.();
                    void navigate('/platform/organizations');
                  }}
                >
                  <span className="size-4 shrink-0" />
                  <span className="truncate">{t.switcher.allOrganizations}</span>
                </Menu.Item>
              </>
            )}

            {defaultError && (
              <p role="alert" className="text-danger px-2.5 py-1.5 text-[12px]">
                {defaultError}
              </p>
            )}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
